// Higher-level confirmation dialog. Built on Dialog. Use this rather than
// window.confirm() — it themes correctly and supports a destructive
// variant.

import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from './Dialog';

export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: ReactNode;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Autofocus the cancel button on destructive prompts (safer default
    // — Enter cancels, doesn't confirm). Confirm button on primary
    // prompts.
    if (open && !destructive) confirmRef.current?.focus();
  }, [open, destructive]);

  return (
    <Dialog
      open={open}
      onClose={loading ? () => undefined : onCancel}
      // Destructive actions don't dismiss on backdrop click — too easy
      // to lose a confirm modal accidentally.
      dismissOnBackdrop={!destructive && !loading}
      ariaLabel={typeof title === 'string' ? title : undefined}
    >
      <DialogHeader>{title}</DialogHeader>
      <DialogBody>{children}</DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          ref={confirmRef}
          variant={destructive ? 'danger' : 'primary'}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
